// src/pages/home/homeView.ts
// Regras de apresentação da Home, isoladas do JSX para serem testáveis sem
// browser (`playwright.unit.config.ts`). Nenhuma delas faz I/O.

import type { UsuarioParametrosAprovacao } from '@/types/aprovacao/Aprovacao';
import type { ContagemPendencia } from '@/hooks/useHomePendencias';

/** Máximo de chips de categoria exibidos antes de resumir em "e outras X". */
export const LIMITE_CATEGORIAS_EXIBIDAS = 5;

/** Mesmo texto que `ApproverParamsSection` usa para código órfão no ERP. */
const NOME_ORFAO = '(sem nome)';

export interface CategoriasResumo {
  /** Nomes a exibir como chip (no máximo `LIMITE_CATEGORIAS_EXIBIDAS`). */
  exibidas: string[];
  /** Quantas categorias permitidas existem além das exibidas. 0 = não há resto. */
  ocultas: number;
}

/**
 * Resume a lista de categorias permitidas em até `limite` chips + o resto.
 *
 * Devolve `null` quando o bloco NÃO deve renderizar:
 *  - `null` no contrato do backend significa **todas as categorias**, ou seja
 *    ausência de restrição — não é informação de limite e não deve virar chip;
 *  - lista vazia não tem nada a exibir (regra "só dados acima de zero").
 */
export const resumirCategorias = (
  categorias: UsuarioParametrosAprovacao['categoriasPermitidas'],
  limite: number = LIMITE_CATEGORIAS_EXIBIDAS
): CategoriasResumo | null => {
  if (!categorias || categorias.length === 0) return null;
  return {
    exibidas: categorias.slice(0, limite).map((c) => c.nomeCategoria || NOME_ORFAO),
    ocultas: Math.max(0, categorias.length - limite),
  };
};

/**
 * Aplica a regra "só dados acima de zero" a uma contagem.
 *
 * `true` = a linha deve aparecer. Zero é ocultado, mas **erro não é zero**:
 * uma falha de rede continua visível (como `—`), porque transformar falha em
 * "tudo em dia" numa fila de aprovação é a mentira mais cara desta tela.
 */
export const devePendenciaAparecer = (contagem: ContagemPendencia | null): boolean => {
  if (!contagem) return false;                      // sem acesso
  if (contagem.status === 'error') return true;     // erro é visível, como '—'
  if (contagem.status === 'loading') return false;  // ainda não se sabe
  return (contagem.total ?? 0) > 0;
};

/** `true` quando o limite de aprovação deve aparecer (0/null = sem limite definido). */
export const devePrecoMaximoAparecer = (precoMaximo: number | null | undefined): boolean =>
  typeof precoMaximo === 'number' && Number.isFinite(precoMaximo) && precoMaximo > 0;

/**
 * `true` quando o card de pendências não tem nenhuma linha para mostrar, embora
 * o usuário TENHA acesso a pelo menos uma — é o estado "Tudo em dia".
 *
 * Distinto de "sem acesso a nenhuma" (aí o card inteiro não existe): sem esse
 * estado o usuário não conseguiria distinguir "nada pendente" de "a Home
 * quebrou".
 */
export const estaTudoEmDia = (contagens: (ContagemPendencia | null)[]): boolean => {
  const comAcesso = contagens.filter((c): c is ContagemPendencia => c !== null);
  if (!comAcesso.length) return false;
  return comAcesso.every((c) => c.status === 'ok' && (c.total ?? 0) === 0);
};
